import {
  CalendarIcon,
  CheckCircleIcon,
  type Icon,
  NumberIcon,
  SingleSelectIcon,
  TypographyIcon,
} from '@primer/octicons-react'

import {defaultFilterProviderOptions, NOT_SHOWN} from '../Filter'
import type {FilterKey, FilterProvider, FilterSuggestion, OptionalKey, SuppliedFilterProviderOptions} from '../types'
import {FilterProviderType} from '../types'
import {BooleanFilterProvider} from './boolean'
import {DateFilterProvider} from './date'
import {KeyOnlyFilterProvider} from './key-only'
import {NumericFilterProvider} from './numeric'
import {SelectFilterProvider} from './select'
import {StaticFilterProvider} from './static'

type BaseSubFilterKey = OptionalKey<Omit<FilterKey, 'priority'>, 'icon'> & {
  type: FilterProviderType
  icon?: Icon
  values?: FilterSuggestion[]
}

type SelectSubFilterKey = BaseSubFilterKey & {
  type: 'select'
  values: FilterSuggestion[]
}

type FixedSubFilterKey = BaseSubFilterKey & {
  type: 'boolean' | 'text' | 'date' | 'number'
}

type MultiLevelKey = {
  subKeys: Array<SelectSubFilterKey | FixedSubFilterKey>
} & FilterKey

const subKeyIcons = {
  boolean: CheckCircleIcon,
  select: SingleSelectIcon,
  text: TypographyIcon,
  date: CalendarIcon,
  number: NumberIcon,
}

export class NestedFilterProvider {
  subKeys: BaseSubFilterKey[]
  filterProviders: FilterProvider[]

  constructor(filterKey: MultiLevelKey, options?: SuppliedFilterProviderOptions) {
    const {subKeys, ...rest} = filterKey
    this.subKeys = subKeys

    this.filterProviders = subKeys.map(subKey => {
      const subKeyObject = {
        ...subKey,
        type: subKey.type,
        key: `${rest.key}.${subKey.key}`,
        displayName: subKey.displayName ?? subKey.key,
        description: subKey.description,
        priority: NOT_SHOWN,
        icon: subKey.icon ?? subKeyIcons[subKey.type],
      }

      if (subKey.type === FilterProviderType.Boolean) {
        return new BooleanFilterProvider(subKeyObject, undefined, options)
      } else if (subKey.type === FilterProviderType.Select) {
        return new SelectFilterProvider(subKeyObject, subKey.values, {
          ...subKey.options,
          filterTypes: {
            ...defaultFilterProviderOptions.filterTypes,
            ...subKey.options?.filterTypes,
          },
        })
      } else if (subKey.type === FilterProviderType.Text) {
        return new KeyOnlyFilterProvider(subKeyObject, subKey.options)
      } else if (subKey.type === FilterProviderType.Date) {
        return new DateFilterProvider(subKeyObject, subKey.values || [], {
          ...subKey.options,
          filterTypes: {
            ...defaultFilterProviderOptions.filterTypes,
            ...subKey.options?.filterTypes,
          },
        })
      } else if (subKey.type === FilterProviderType.Number) {
        return new NumericFilterProvider(subKeyObject, subKey.values || [], {
          ...subKey.options,
          filterTypes: {
            ...defaultFilterProviderOptions.filterTypes,
            ...subKey.options?.filterTypes,
          },
        })
      } else {
        // Fallback to StaticFilterProvider for any unknown types
        const staticFilterProvider = new StaticFilterProvider(subKeyObject, subKey.values || [], {
          ...subKey.options,
          filterTypes: {
            ...defaultFilterProviderOptions.filterTypes,
            ...subKey.options?.filterTypes,
          },
        })
        staticFilterProvider.type = subKeyObject.type
        return staticFilterProvider
      }
    })

    this.filterProviders.push(new StaticFilterProvider({...rest, key: `${rest.key}.`}, [], options))
  }
}
